import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight, ArrowRight, Search } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Page } from '@/components/AppShell';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  Select,
  Stat,
} from '@/components/ui/primitives';
import { cn, formatRelative } from '@/lib/utils';
import type { AssignmentLeadDto } from '@/lib/api';
import { OPPORTUNITY_TYPES,OPPORTUNITY_LABELS,type OpportunityType } from '@shared/opportunities.js';

/**
 * Assignments — the one genuinely new surface in the Phase 7 mockups.
 *
 * No new capability: assign, bulk assign, assign-by-filter, unassign and the
 * per-user counts all existed and are exercised here through the same Phase 2
 * endpoints. What did not exist was a place to answer "who has what, and what
 * has nobody" without visiting three screens.
 *
 * Layout is the approved one: pool ~62%, workload ~38%, and the transfer
 * controls as a selection bar *inside* the pool rather than a permanent middle
 * column — a control that acts on a selection belongs beside the selection.
 *
 * Deliberately not invented: round robin, territories, quotas, capacity rules,
 * automatic reassignment. Deliberately absent metrics: "worked this week" and
 * "returned to pool" from the mockup. Calls are attributed to an agent rather
 * than a user account, so neither can be derived per VA without inventing the
 * mapping — and a made-up number on an operations screen is worse than none.
 */
export function AssignmentsPage(): JSX.Element {
  const queryClient = useQueryClient();

  const [search, setSearch] = React.useState('');
  const [appliedSearch, setAppliedSearch] = React.useState('');
  const [ownerNames, setOwnerNames] = React.useState<'all' | 'with' | 'without'>('all');
  const [opportunityType,setOpportunityType]=React.useState<OpportunityType|''>('');
  /**
   * Two separate choices, deliberately.
   *
   * `viewing` is whose workload the right-hand panel shows, and it may be a
   * deactivated VA — otherwise the leads a deactivated account still holds would
   * be invisible and unreclaimable. `assignTo` is who receives new work, and the
   * server only permits an active VA, so the picker only offers those.
   */
  const [viewing, setViewing] = React.useState<number | null>(null);
  const [assignTo, setAssignTo] = React.useState<number | null>(null);
  const [picked, setPicked] = React.useState<Set<number>>(new Set());
  const [returning, setReturning] = React.useState<Set<number>>(new Set());
  const [returnAllUser, setReturnAllUser] = React.useState<number | null>(null);
  const allWorkloadSelected = viewing !== null && returnAllUser === viewing;

  const overview = useQuery({
    queryKey: ['assignment-overview', viewing, appliedSearch, ownerNames,opportunityType],
    queryFn: () =>
      api.assignmentOverview({
        userId: viewing ?? undefined,
        search: appliedSearch || undefined,
        hasOwnerName: ownerNames === 'all' ? undefined : ownerNames === 'with',
        opportunityType:opportunityType||undefined,
      }),
  });

  const data = overview.data;
  const vas = data?.vas ?? [];
  const assignable = vas.filter((v) => v.isActive);

  // Default both pickers once the list arrives, so the common one-VA case needs
  // no clicks. Viewing falls back to anyone holding work — including a
  // deactivated account, which is exactly the workload an admin needs to find.
  React.useEffect(() => {
    if (viewing === null && vas.length > 0) {
      setViewing((assignable[0] ?? vas.find((v) => v.assignedTotal > 0) ?? vas[0]).userId);
    }
    if (assignTo === null && assignable.length > 0) setAssignTo(assignable[0].userId);
  }, [vas, assignable, viewing, assignTo]);

  const refresh = async () => {
    setPicked(new Set());
    setReturning(new Set());
    setReturnAllUser(null);
    await queryClient.invalidateQueries({ queryKey: ['assignment-overview'] });
    // The lead list and the user counts both show assignment state.
    await queryClient.invalidateQueries({ queryKey: ['leads'] });
    await queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  const assign = useMutation({
    mutationFn: ({ ids, userId }: { ids: number[]; userId: number }) => api.assignLeads(ids, userId),
    onSuccess: async (result) => {
      toast.success(`Assigned ${result.assigned} lead${result.assigned === 1 ? '' : 's'}.`);
      await refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const unassign = useMutation({
    mutationFn: (selection: number[] | { userId: number }) => Array.isArray(selection)
      ? api.unassignLeads(selection) : api.unassignUserLeads(selection.userId),
    onSuccess: async (result) => {
      toast.success(
        `Returned ${result.unassigned} lead${result.unassigned === 1 ? '' : 's'} to the pool.`,
      );
      await refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const busy = assign.isPending || unassign.isPending;

  if (overview.isLoading) {
    return (
      <Page title="Assignments">
        <LoadingState />
      </Page>
    );
  }
  if (overview.error) {
    return (
      <Page title="Assignments">
        <ErrorState error={overview.error} onRetry={() => overview.refetch()} />
      </Page>
    );
  }

  const pool = data?.unassigned ?? [];
  const matchingIds = data?.unassignedIds ?? [];
  const selected = data?.selected ?? null;

  // Who is actually holding work, so the Assigned hint describes the number
  // above it rather than a different population.
  const holders = vas.filter((v) => v.assignedTotal > 0);
  const deactivatedHolders = holders.filter((v) => !v.isActive).length;
  const holdersHint =
    holders.length === 0
      ? 'nobody is holding leads'
      : `across ${holders.length} VA${holders.length === 1 ? '' : 's'}` +
        (deactivatedHolders > 0 ? `, ${deactivatedHolders} deactivated` : '');

  const togglePick = (id: number) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleReturn = (id: number) => {
    setReturnAllUser(null);
    setReturning((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const returnCount = allWorkloadSelected ? (selected?.assignedTotal ?? 0) : returning.size;
  const toggleWorkload = () => {
    setReturnAllUser(allWorkloadSelected ? null : viewing);
    setReturning(new Set());
  };

  const allPoolPicked = pool.length > 0 && pool.every((l) => picked.has(l.id));

  return (
    <Page
      title="Assignments"
      description={
        data
          ? `${data.unassignedTotal.toLocaleString()} of ${data.activeTotal.toLocaleString()} active leads are unassigned`
          : undefined
      }
    >
      <div className="flex h-full min-h-0 flex-col gap-4 p-5 md:p-6">
        {/* Only figures the database can actually produce. */}
        <div className="grid shrink-0 grid-cols-2 gap-3 md:grid-cols-3">
          <Stat
            label="Unassigned"
            value={(data?.unassignedTotal ?? 0).toLocaleString()}
            hint={`of ${(data?.activeTotal ?? 0).toLocaleString()} active`}
            valueClassName={data && data.unassignedTotal > 0 ? 'text-amber-300' : undefined}
          />
          {/*
            The hint counts every account actually holding work, deactivated ones
            included, because the value above does. Saying "across N active VAs"
            while the total included a deactivated account's leads was a quiet
            lie, and on this screen a deactivated holder is the interesting case.
          */}
          <Stat
            label="Assigned"
            value={vas.reduce((sum, v) => sum + v.assignedTotal, 0).toLocaleString()}
            hint={holdersHint}
          />
          <Stat
            label={selected ? selected.displayName : 'Selected VA'}
            value={selected ? selected.assignedTotal.toLocaleString() : '—'}
            hint={selected ? 'currently holding' : 'no VA selected'}
          />
        </div>

        {assignable.length === 0 && (
          <Card>
            <CardBody>
              <p className="text-xs text-muted-foreground">
                There are no active VA accounts yet, so nothing can be assigned. Create one in
                Settings → Access first.
              </p>
            </CardBody>
          </Card>
        )}

        {/*
          62/38, at every scale. The pool holds the long business names and is
          where the work happens; the workload panel is a scan target and needs
          less width. Phase 7 approved this side-by-side at 140% as well, so it
          is not stacked at enlarged scale — the columns inside each table absorb
          the growth instead (Business flexes, the narrow columns collapse to
          their content, and the selection bar wraps).
        */}
        <div className="grid min-h-0 flex-1 grid-cols-[62fr_38fr] gap-3">
          {/* ---- Unassigned pool ------------------------------------------ */}
          <Card className="flex min-h-0 flex-col">
            <CardHeader className="flex-row items-center justify-between gap-2">
              <CardTitle>
                Unassigned pool
                <span className="ml-2 font-normal text-muted-foreground">
                  {/* The pool is what the search filters, so it counts matches. */}
                  {(data?.matchingTotal ?? 0).toLocaleString()} lead
                  {data?.matchingTotal === 1 ? '' : 's'}
                  {appliedSearch ? ` matching “${appliedSearch}”` : ''}
                </span>
              </CardTitle>
              <form
                className="flex items-center gap-1.5"
                onSubmit={(event) => {
                  event.preventDefault();
                  setPicked(new Set());
                  setAppliedSearch(search.trim());
                }}
              >
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search pool"
                  className="h-7 w-40 text-xs"
                />
                <Button type="submit" size="sm" variant="outline">
                  <Search className="h-3.5 w-3.5" />
                  Find
                </Button>
                {appliedSearch && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setSearch('');
                      setAppliedSearch('');
                      setPicked(new Set());
                    }}
                  >
                    Clear
                  </Button>
                )}
              </form>
            </CardHeader>

            {/*
              The selection bar: attached to the table it acts on, and wrapping
              at enlarged scale rather than squeezing the rows.
            */}
            <div className="flex shrink-0 flex-wrap items-center gap-x-2 gap-y-1.5 border-b border-primary/30 bg-primary/[0.06] px-3 py-2">
              <label htmlFor="assignment-opportunity-filter" className="text-xs text-muted-foreground">Opportunity type</label>
              <Select id="assignment-opportunity-filter" value={opportunityType} disabled={busy||overview.isFetching}
                onChange={event=>{setPicked(new Set());setReturning(new Set());setReturnAllUser(null);setOpportunityType(event.target.value as OpportunityType|'');}} className="h-7 w-44 text-xs">
                <option value="">All opportunity types</option>
                {OPPORTUNITY_TYPES.map(type=><option key={type} value={type}>{OPPORTUNITY_LABELS[type]}</option>)}
              </Select>
              <label htmlFor="assignment-owner-filter" className="text-xs text-muted-foreground">Owner name</label>
              <Select id="assignment-owner-filter" value={ownerNames} disabled={busy || overview.isFetching}
                onChange={(event) => {
                  setPicked(new Set());
                  setOwnerNames(event.target.value as 'all' | 'with' | 'without');
                }} className="h-7 w-44 text-xs">
                <option value="all">All leads</option>
                <option value="with">With owner name</option>
                <option value="without">Without owner name</option>
              </Select>
              <span className="whitespace-nowrap text-xs font-medium">
                {picked.size} selected
              </span>
              <span className="mx-0.5 h-3.5 w-px bg-border" />
              <span className="whitespace-nowrap text-2xs text-muted-foreground">Assign to</span>
              {/* Active VAs only: the server refuses to assign to a deactivated account. */}
              <Select
                value={assignTo ?? ''}
                onChange={(e) => setAssignTo(e.target.value ? Number(e.target.value) : null)}
                className="h-7 w-40 text-xs"
              >
                <option value="">Choose a VA…</option>
                {assignable.map((v) => (
                  <option key={v.userId} value={v.userId}>
                    {v.displayName} ({v.assignedTotal})
                  </option>
                ))}
              </Select>
              <Button
                size="sm"
                disabled={busy || overview.isFetching || !assignTo || picked.size === 0}
                onClick={() => assign.mutate({ ids: [...picked], userId: assignTo! })}
              >
                Assign {picked.size || ''} selected
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={busy || overview.isFetching || !assignTo || matchingIds.length === 0}
                onClick={() => {
                  if (
                    !window.confirm(
                      `Assign all ${matchingIds.length} unassigned leads matching the current filters and search?`,
                    )
                  )
                    return;
                  assign.mutate({ ids: matchingIds, userId: assignTo! });
                }}
              >
                Assign all {matchingIds.length} matching
              </Button>
              <p className="w-full text-2xs leading-snug text-muted-foreground">
                <strong className="font-medium text-foreground">All matching</strong> is resolved on
                the server from the current filters and search, not from the rows on screen.
              </p>
            </div>

            <div className="min-h-0 flex-1 overflow-auto">
              {pool.length === 0 ? (
                <EmptyState
                  title="Nothing unassigned"
                  description={
                    appliedSearch
                      ? 'No unassigned lead matches that search.'
                      : 'Every active lead currently belongs to someone.'
                  }
                />
              ) : (
                <table className="data-table text-xs">
                  <thead className="sticky top-0 z-10 bg-card text-left text-muted-foreground">
                    <tr className="border-b border-border">
                      <th className="w-8 py-1.5 pl-3">
                        <input
                          type="checkbox"
                          aria-label="Select all shown"
                          checked={allPoolPicked}
                          onChange={() =>
                            setPicked(allPoolPicked ? new Set() : new Set(pool.map((l) => l.id)))
                          }
                        />
                      </th>
                      <th className="w-full py-1.5 pr-3 font-medium">Business</th>
                      <th className="whitespace-nowrap py-1.5 pr-3 font-medium">Location</th>
                      <th className="whitespace-nowrap py-1.5 pr-3 text-right font-medium">Score</th>
                      <th className="whitespace-nowrap py-1.5 pr-3 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pool.map((lead) => (
                      <tr
                        key={lead.id}
                        className={cn(
                          'border-b border-border/60',
                          picked.has(lead.id) && 'bg-primary/[0.07]',
                        )}
                      >
                        <td className="py-1.5 pl-3 align-top">
                          <input
                            type="checkbox"
                            aria-label={`Select ${lead.businessName}`}
                            checked={picked.has(lead.id)}
                            onChange={() => togglePick(lead.id)}
                          />
                        </td>
                        {/* Wraps rather than truncates: two businesses must stay tellable apart. */}
                        <td className="py-1.5 pr-3 font-medium leading-snug">
                          {lead.businessName}
                          {lead.ownerName && <span className="mt-1 block text-xs font-normal text-muted-foreground">Owner: {lead.ownerName}</span>}
                        </td>
                        <td className="whitespace-nowrap py-1.5 pr-3 align-top text-muted-foreground">
                          {[lead.city, lead.state].filter(Boolean).join(', ') || '—'}
                        </td>
                        <td className="py-1.5 pr-3 text-right align-top font-semibold tabular-nums">
                          {lead.normalizedLeadScore ?? '—'}
                        </td>
                        <td className="whitespace-nowrap py-1.5 pr-3 align-top">
                          <Badge>{lead.pipelineStatus}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="shrink-0 border-t border-border px-3 py-1.5 text-2xs text-muted-foreground">
              Showing {pool.length.toLocaleString()} of {(data?.matchingTotal ?? 0).toLocaleString()}
              {appliedSearch ? ' matching' : ''}
              {picked.size > 0 ? ` · ${picked.size} selected` : ''}
            </div>
          </Card>

          {/* ---- VA workload ---------------------------------------------- */}
          <Card className="flex min-h-0 flex-col">
            <CardHeader className="flex-col items-stretch gap-1.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle>
                  Workload
                  {selected && (
                    <span className="ml-2 font-normal text-muted-foreground">
                      {selected.assignedTotal.toLocaleString()} assigned
                    </span>
                  )}
                </CardTitle>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy || overview.isFetching || returnCount === 0}
                  onClick={() => {
                    if (!window.confirm(`Return ${allWorkloadSelected ? 'all ' : ''}${returnCount} ${allWorkloadSelected ? `leads assigned to ${selected?.displayName}` : 'selected leads'} to the unassigned pool? Assignment history will be kept.`)) return;
                    unassign.mutate(allWorkloadSelected ? { userId: selected!.userId } : [...returning]);
                  }}
                >
                  <ArrowLeftRight className="h-3.5 w-3.5" />
                  Return {allWorkloadSelected ? 'all ' : ''}{returnCount || ''} to pool
                </Button>
              </div>
              {/*
                Every VA, including deactivated ones. A deactivated account can
                still be holding leads, and those are precisely the ones an admin
                needs to find and return.
              */}
              <div className="flex flex-wrap items-center gap-1.5">
                <Select
                  aria-label="View caller workload"
                  value={viewing ?? ''}
                  onChange={(e) => {
                    setViewing(e.target.value ? Number(e.target.value) : null);
                    setReturning(new Set());
                    setReturnAllUser(null);
                  }}
                  className="h-7 w-44 text-xs"
                >
                  <option value="">Choose a VA…</option>
                  {vas.map((v) => (
                    <option key={v.userId} value={v.userId}>
                      {v.displayName}
                      {v.isActive ? '' : ' (deactivated)'} ({v.assignedTotal})
                    </option>
                  ))}
                </Select>
                <Button size="sm" variant="outline"
                  disabled={busy || overview.isFetching || !selected?.assignedTotal}
                  onClick={toggleWorkload}>
                  {allWorkloadSelected ? 'Clear selection' : `Select all ${selected?.assignedTotal ?? 0}`}
                </Button>
                {returnCount > 0 && <span aria-live="polite" className="text-xs text-muted-foreground">
                  {returnCount.toLocaleString()} selected{allWorkloadSelected ? ' across entire workload' : ''}
                </span>}
                {selected && !selected.isActive && (
                  <Badge variant="warning">
                    Deactivated — cannot receive new work
                  </Badge>
                )}
              </div>
            </CardHeader>

            <div className="min-h-0 flex-1 overflow-auto">
              {!selected ? (
                <EmptyState
                  title="No VA selected"
                  description="Choose a VA above to see what they are holding."
                />
              ) : selected.leads.length === 0 ? (
                <EmptyState
                  title="Nothing assigned"
                  description={`${selected.displayName} is not holding any active leads.`}
                />
              ) : (
                <table className="data-table text-xs">
                  <thead className="sticky top-0 z-10 bg-card text-left text-muted-foreground">
                    <tr className="border-b border-border">
                      <th className="w-8 py-1.5 pl-3">
                        <input type="checkbox" aria-label={`Select all leads assigned to ${selected.displayName}`}
                          checked={allWorkloadSelected} disabled={busy || overview.isFetching}
                          onChange={toggleWorkload} />
                      </th>
                      <th className="w-full py-1.5 pr-3 font-medium">Business</th>
                      <th className="whitespace-nowrap py-1.5 pr-3 text-right font-medium">Calls</th>
                      <th className="whitespace-nowrap py-1.5 pr-3 text-right font-medium">Last</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.leads.map((lead: AssignmentLeadDto) => (
                      <tr
                        key={lead.id}
                        className={cn(
                          'border-b border-border/60',
                          (allWorkloadSelected || returning.has(lead.id)) && 'bg-primary/[0.07]',
                        )}
                      >
                        <td className="py-1.5 pl-3 align-top">
                          <input
                            type="checkbox"
                            aria-label={`Return ${lead.businessName} to the pool`}
                            checked={allWorkloadSelected || returning.has(lead.id)}
                            disabled={busy || overview.isFetching || allWorkloadSelected}
                            onChange={() => toggleReturn(lead.id)}
                          />
                        </td>
                        {/*
                          Status sits under the name rather than taking a column:
                          in a narrow panel that column costs more than it says.
                        */}
                        <td className="py-1.5 pr-3">
                          <span className="block font-medium leading-snug">{lead.businessName}</span>
                          {lead.ownerName && <span className="mt-1 block text-xs text-muted-foreground">Owner: {lead.ownerName}</span>}
                          <Badge className="mt-0.5">{lead.pipelineStatus}</Badge>
                        </td>
                        <td className="py-1.5 pr-3 text-right align-top tabular-nums text-muted-foreground">
                          {lead.attemptCount}
                        </td>
                        <td className="whitespace-nowrap py-1.5 pr-3 text-right align-top text-muted-foreground">
                          {lead.lastContactedAt ? formatRelative(lead.lastContactedAt) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="shrink-0 border-t border-border px-3 py-1.5 text-2xs text-muted-foreground">
              {selected
                ? `Showing ${selected.leads.length.toLocaleString()} of ${selected.assignedTotal.toLocaleString()} · least recently contacted first`
                : 'Returning a lead keeps its assignment history; it deletes nothing.'}
            </div>
          </Card>
        </div>
      </div>
    </Page>
  );
}
